import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "summit-scents-cart";
const MAX_PER_PURCHASE = 2;

const PRICING = {
  ivory: { id: "ivory", name: "No. 1 - Ivory", size: "3.4oz (100ml)", price: 59.99, maxQty: 2, image: "/no1-ivory-bottle.jpg", tagline: "Eau de Parfum", category: "men" },
  "ivory-1-7oz": { id: "ivory-1-7oz", name: "No. 1 - Ivory", size: "1.7oz (50ml)", price: 32.99, maxQty: 2, image: "/no1-ivory-bottle.jpg", tagline: "Eau de Parfum", category: "men" },
  bronze: { id: "bronze", name: "No. 2 - Bronze", size: "3.4oz (100ml)", price: 55.99, maxQty: 2, image: "/no2-bronze-bottle.jpg", tagline: "Eau de Parfum", category: "men" },
  "qzip-black": { id: "qzip-black", name: "Summit Scents Quarter-Zip — Black", size: "Unisex Fit", price: 47.99, maxQty: 5, hasSize: true, image: "/qzip-black.jpg", tagline: "Performance Quarter-Zip", category: "apparel" },
  "qzip-white": { id: "qzip-white", name: "Summit Scents Quarter-Zip — White", size: "Unisex Fit", price: 47.99, maxQty: 5, hasSize: true, image: "/qzip-white.jpg", tagline: "Performance Quarter-Zip", category: "apparel" },
};

const lineKey = (productId, size) => `${productId}::${size || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [promo, setPromo] = useState(null); // { code, flash_price, applies_to_categories, label }

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((productId, qty = 1, size = null) => {
    const max = (PRICING[productId]?.maxQty) ?? MAX_PER_PURCHASE;
    setItems((prev) => {
      const key = lineKey(productId, size);
      const existing = prev.find((i) => lineKey(i.product_id, i.size) === key);
      if (existing) {
        const next = Math.min(existing.quantity + qty, max);
        return prev.map((i) =>
          lineKey(i.product_id, i.size) === key ? { ...i, quantity: next } : i,
        );
      }
      return [...prev, { product_id: productId, quantity: Math.min(qty, max), size }];
    });
  }, []);

  const updateQty = useCallback((productId, quantity, size = null) => {
    const max = (PRICING[productId]?.maxQty) ?? MAX_PER_PURCHASE;
    const key = lineKey(productId, size);
    setItems((prev) =>
      prev
        .map((i) =>
          lineKey(i.product_id, i.size) === key
            ? { ...i, quantity: Math.max(0, Math.min(quantity, max)) }
            : i,
        )
        .filter((i) => i.quantity > 0),
    );
  }, []);

  const removeItem = useCallback((productId, size = null) => {
    const key = lineKey(productId, size);
    setItems((prev) => prev.filter((i) => lineKey(i.product_id, i.size) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const enrichedItems = useMemo(
    () =>
      items.map((i) => {
        const p = PRICING[i.product_id] || { name: i.product_id, size: "", price: 0, maxQty: MAX_PER_PURCHASE };
        const basePrice = p.price;
        const applicable = promo && p.category && promo.applies_to_categories?.includes(p.category);
        const effective = applicable ? Math.min(basePrice, promo.flash_price) : basePrice;
        const discounted = applicable && effective < basePrice;
        return {
          ...i,
          name: p.name,
          size: i.size || p.size,
          displaySize: i.size || p.size,
          price: effective,
          basePrice,
          discounted,
          maxQty: p.maxQty ?? MAX_PER_PURCHASE,
          image: p.image,
          tagline: p.tagline,
          category: p.category,
          lineTotal: effective * i.quantity,
          baseLineTotal: basePrice * i.quantity,
          lineKey: lineKey(i.product_id, i.size),
        };
      }),
    [items, promo],
  );

  const subtotal = useMemo(
    () => enrichedItems.reduce((sum, i) => sum + i.lineTotal, 0),
    [enrichedItems],
  );

  const originalSubtotal = useMemo(
    () => enrichedItems.reduce((sum, i) => sum + i.baseLineTotal, 0),
    [enrichedItems],
  );

  const discountAmount = useMemo(
    () => Math.max(0, originalSubtotal - subtotal),
    [originalSubtotal, subtotal],
  );

  const totalQty = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items],
  );

  const value = {
    items,
    enrichedItems,
    subtotal,
    originalSubtotal,
    discountAmount,
    promo,
    applyPromo: setPromo,
    clearPromo: () => setPromo(null),
    totalQty,
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    addItem,
    updateQty,
    removeItem,
    clear,
    MAX_PER_PURCHASE,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
